// eslint-disable-next-line no-unused-vars
import React, { useState, useRef } from 'react'
import { NavLink } from 'react-router-dom'
import { ajaxPerso, parseAjaxReturn, clipboard } from '../../../fnc'
import store from 'store2'
import loading from '../../../Rolling.svg'
import {
	BCOM_BOOKMARKLET_DELIVERY_HREF,
	BCOM_BOOKMARKLET_DATASET_HREF,
	DELIVERY_CONSOLE_SCRIPT,
	extractDeliveryToken,
} from './bcomTokenBookmarklets'

const refConst = require('../../../constants.js').v

const ALLOWED_UIDS = [
	'N7nLPzs2MVc8l2wbWtp4PT4Oiot1',
	'bdPQVmUCs3UhT8TepjFDPipBzIu2',
	'4XTCWbwcFscWYkXaOXQeSbVqE6o2',
]

const STORE_KEYS = {
	dataset: 'bcomPipeline.datasetToken',
	delivery: 'bcomPipeline.deliveryToken',
}

const BCOM_URLS = {
	delivery:
		'https://order.3m.com/bcomv2/v2/bComUSSite/users/uxntltwp/productdetails/productAvailability?materialIDs=7100240025&includePrimaryShipping=true&includeVCFlags=true&includePromoData=true',
	datasetHelp:
		'https://order.3m.com/',
}

const STEPS = [
	{ id: 'upload', label: '1. Upload b.com Excel' },
	{ id: 'convert', label: '2. Convert to JSON' },
	{ id: 'import', label: '3. Import missing products' },
	{ id: 'downloadDataset', label: '4a. Download dataset (Bearer)' },
	{ id: 'downloadDelivery', label: '4b. Download delivery (saml)' },
]

/** Strip Bearer prefix or extract samlPassThroughToken from a cookie header paste */
const normalizeDatasetToken = (raw) => {
	let t = (raw || '').trim()
	if(/^bearer\s+/i.test(t))	t = t.replace(/^bearer\s+/i, '')
	return t.trim()
}

const normalizeDeliveryToken = extractDeliveryToken

const CHUNK_SIZE = 5 * 1024 * 1024 // stay under typical 8M post_max_size

const ajaxLong = (args, callback, failCallback) => {
	ajaxPerso(args, callback, failCallback, {
		timeout: 600000,
	})
}

const uploadChunk = (formData) => new Promise((resolve, reject) => {
	ajaxPerso(formData, resolve, (jqXHR, textStatus) => reject(new Error(textStatus)), {
		contentType: false,
		processData: false,
		cache: false,
		timeout: 120000,
	})
})

export default function BcomPipeline(props) {
	const fileInput = useRef(null)
	const [file, setFile] = useState(null)
	const [fileName, setFileName] = useState(null)
	const [stepStatus, setStepStatus] = useState({
		upload: null, convert: null, import: null, downloadDataset: null, downloadDelivery: null,
	})
	const [datasetToken, setDatasetToken] = useState(() => store(STORE_KEYS.dataset) || '')
	const [deliveryToken, setDeliveryToken] = useState(() => store(STORE_KEYS.delivery) || '')
	const [fetchDump, setFetchDump] = useState('')
	const [running, setRunning] = useState(null)
	const [log, setLog] = useState([])

	const appendLog = (msg, type = 'info') => {
		setLog(prev => [...prev, { msg, type, at: new Date().toLocaleTimeString() }])
	}

	const setStep = (step, status) => {
		setStepStatus(prev => ({ ...prev, [step]: status }))
	}

	if(!props.securityClearance(props))	return ''
	if(ALLOWED_UIDS.indexOf(props.getUser().uid) === -1){
		return <div className="main ctnPage p-4"><div className="alert alert-warning">You do not have access to this tool.</div></div>
	}

	const onFileChange = (e) => {
		const f = e.target.files[0]
		if(!f)	return
		if(!/\.xlsx$/i.test(f.name)){
			props.info({ error: 'Please select an .xlsx file (b.com active items export).' })
			return
		}
		setFile(f)
		setFileName(null)
		setStepStatus({ upload: null, convert: null, import: null })
		setLog([])
	}

	const uploadFile = async (callback) => {
		if(!file){
			props.info({ error: 'Please select a file first.' })
			return
		}
		if(props.loading)	props.loading(true)
		setRunning('upload')
		setStep('upload', 'running')

		const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE))
		const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
		appendLog(`Uploading ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB, ${totalChunks} chunk${totalChunks > 1 ? 's' : ''})…`)

		try {
			for(let i = 0; i < totalChunks; i++){
				const start = i * CHUNK_SIZE
				const end = Math.min(start + CHUNK_SIZE, file.size)
				const blob = file.slice(start, end)

				const formData = new FormData()
				formData.append('file', blob, file.name)
				formData.append('api', 'autoCreator')
				formData.append('trigger', 'upload_bcom_dump_chunk')
				formData.append('token', refConst.tokenEio)
				formData.append('isWebRequest', true)
				formData.append('uploadId', uploadId)
				formData.append('fileName', file.name)
				formData.append('chunkIndex', String(i))
				formData.append('totalChunks', String(totalChunks))

				if(totalChunks > 1)
					appendLog(`Chunk ${i + 1} / ${totalChunks}…`)

				const res = await uploadChunk(formData)
				if(!res || !res.success){
					throw new Error(res?.error || 'Upload failed')
				}
				if(res.complete){
					setFileName(res.fileName)
					setStep('upload', 'done')
					appendLog(`Saved as ${res.fileNameFull}`, 'success')
					setRunning(null)
					if(props.loading)	props.loading(false)
					callback && callback(res)
					return
				}
			}
			throw new Error('Upload did not complete')
		}catch(err){
			setRunning(null)
			if(props.loading)	props.loading(false)
			setStep('upload', 'error')
			const msg = err.message || String(err)
			appendLog(msg, 'error')
			props.info({ error: msg })
		}
	}

	const convertDump = (name, callback) => {
		if(props.loading)	props.loading(true)
		setRunning('convert')
		setStep('convert', 'running')
		appendLog('Converting Excel to JSON (this may take a few minutes)…')

		ajaxLong({
			api: 'autoCreator',
			trigger: 'convert_bcom_dump_to_json',
			fileName: name,
		}, (res) => {
			setRunning(null)
			if(props.loading)	props.loading(false)
			if(res && res.success){
				setStep('convert', 'done')
				appendLog(`JSON written: ${res.outputPath}`, 'success')
				callback && callback(res)
			}else{
				setStep('convert', 'error')
				appendLog(res?.error || 'Conversion failed', 'error')
				props.info({ error: res?.error || 'Conversion failed' })
			}
		}, (jqXHR, textStatus) => {
			setRunning(null)
			if(props.loading)	props.loading(false)
			setStep('convert', 'error')
			appendLog(textStatus, 'error')
			props.info({ error: textStatus })
		})
	}

	const saveTokens = () => {
		store(STORE_KEYS.dataset, normalizeDatasetToken(datasetToken))
		store(STORE_KEYS.delivery, normalizeDeliveryToken(deliveryToken))
		props.info({ success: 'Tokens saved in this browser', second: 2 })
	}

	const copyDeliveryConsoleScript = () => {
		clipboard(`(function(){${DELIVERY_CONSOLE_SCRIPT}})();`, props)
	}

	const parseDeliveryFromFetch = () => {
		const t = extractDeliveryToken(fetchDump)
		if(!t || !t.startsWith('1:')){
			props.info({ error: 'Could not find samlPassThroughToken in pasted text. Use Copy → Copy as fetch on the productAvailability request.' })
			return
		}
		setDeliveryToken(t)
		props.info({ success: 'Delivery token extracted', second: 2 })
	}

	const pasteTokenFromClipboard = async (type) => {
		if(!navigator.clipboard?.readText){
			props.info({ error: 'Clipboard read not supported in this browser' })
			return
		}
		try {
			const raw = await navigator.clipboard.readText()
			if(type === 'dataset'){
				setDatasetToken(normalizeDatasetToken(raw))
				props.info({ success: 'Dataset token pasted from clipboard', second: 2 })
			}else{
				setDeliveryToken(normalizeDeliveryToken(raw))
				props.info({ success: 'Delivery token pasted from clipboard', second: 2 })
			}
		}catch(err){
			props.info({ error: 'Could not read clipboard — allow permission or paste manually' })
		}
	}

	const copyCliCommand = (typeData) => {
		const tokenKey = typeData === 'dataset' ? 'datasetToken' : 'deliveryToken'
		const token = typeData === 'dataset'
			? normalizeDatasetToken(datasetToken)
			: normalizeDeliveryToken(deliveryToken)
		if(!token){
			props.info({ error: `Paste the ${typeData} token first` })
			return
		}
		const cmd = `php task/task_multi.php api=eio trigger=download_all_bcom_data verbose=true typeData=${typeData} ${tokenKey}="${token}" old=false`
		clipboard(cmd, props)
	}

	const downloadBcomApi = (typeData, callback) => {
		const stepId = typeData === 'dataset' ? 'downloadDataset' : 'downloadDelivery'
		const token = typeData === 'dataset'
			? normalizeDatasetToken(datasetToken)
			: normalizeDeliveryToken(deliveryToken)

		if(!token){
			props.info({ error: `Paste the ${typeData === 'dataset' ? 'Bearer (dataset)' : 'samlPassThroughToken (delivery)'} token first` })
			return
		}

		saveTokens()
		if(props.loading)	props.loading(true)
		setRunning(stepId)
		setStep(stepId, 'running')
		appendLog(`Downloading b.com ${typeData} data (resumes if interrupted; may take a long time)…`)

		const args = {
			api: 'eio',
			trigger: 'download_all_bcom_data',
			typeData,
			old: false,
		}
		if(typeData === 'dataset')	args.datasetToken = token
		else	args.deliveryToken = token

		ajaxLong(args, (res) => {
			setRunning(null)
			if(props.loading)	props.loading(false)
			if(res && res.success !== false && !res.errors){
				setStep(stepId, 'done')
				appendLog(`${typeData}: completed (${res.skuCount ?? '?'} SKUs in queue)`, 'success')
				callback && callback(res)
			}else{
				setStep(stepId, res?.errors ? 'warn' : 'error')
				const errMsg = Array.isArray(res?.errors) ? res.errors.join('\n') : (res?.error || 'Download finished with issues')
				appendLog(errMsg, res?.errors ? 'warn' : 'error')
				if(res?.result)	appendLog(JSON.stringify(res.result, null, 2).slice(0, 1500), 'info')
				callback && callback(res)
			}
		}, (jqXHR, textStatus) => {
			setRunning(null)
			if(props.loading)	props.loading(false)
			setStep(stepId, 'warn')
			appendLog(`${textStatus} — partial progress may be saved; run this step again to resume.`, 'warn')
			props.info({ error: textStatus })
		})
	}

	const importMissing = (callback) => {
		if(props.loading)	props.loading(true)
		setRunning('import')
		setStep('import', 'running')
		appendLog('Importing missing products into auto_creator_product…')

		ajaxLong({
			api: 'autoCreator',
			trigger: 'upload_missing_product_from_bcom_report',
		}, (res) => {
			setRunning(null)
			if(props.loading)	props.loading(false)
			if(res && !res.errors){
				setStep('import', 'done')
				const summary = parseAjaxReturn(res)
				appendLog(summary || 'Import completed', 'success')
				appendLog(JSON.stringify(res.resInsert || res, null, 2).slice(0, 2000), 'info')
				callback && callback(res)
			}else{
				setStep('import', res?.errors ? 'warn' : 'done')
				appendLog(Array.isArray(res?.errors) ? res.errors.join('\n') : 'Import finished with notes', 'warn')
				callback && callback(res)
			}
		}, (jqXHR, textStatus) => {
			setRunning(null)
			if(props.loading)	props.loading(false)
			setStep('import', 'error')
			appendLog(textStatus, 'error')
			props.info({ error: textStatus })
		})
	}

	const runAll = () => {
		uploadFile((uploadRes) => {
			convertDump(uploadRes.fileName, () => {
				importMissing()
			})
		})
	}

	const runStep = (stepId) => {
		if(stepId === 'upload')	uploadFile()
		else if(stepId === 'convert')	convertDump(fileName)
		else if(stepId === 'import')	importMissing()
		else if(stepId === 'downloadDataset')	downloadBcomApi('dataset')
		else if(stepId === 'downloadDelivery')	downloadBcomApi('delivery')
	}

	const stepBadge = (status) => {
		if(status === 'running')	return <span className="badge badge-secondary ml-2">Running…</span>
		if(status === 'done')	return <span className="badge badge-success ml-2">Done</span>
		if(status === 'error')	return <span className="badge badge-danger ml-2">Error</span>
		if(status === 'warn')	return <span className="badge badge-warning ml-2">Done (warnings)</span>
		return null
	}

	const isBusy = running !== null

	return (
		<div className="main ctnPage p-4">
			<div className="d-flex flex-wrap align-items-center mb-4">
				<NavLink to="/auto_create">
					<button type="button" className="btn btn-outline-primary mr-3">← Back to Auto Creator</button>
				</NavLink>
				<h4 className="mb-0">3M b.com report pipeline</h4>
			</div>

			<p className="text-muted mb-4">
				Upload the b.com <strong>active items</strong> Excel export, convert it to JSON, import missing SKUs, then download product data from 3M&apos;s private APIs (step 4 requires fresh session tokens).
			</p>

			<div className="card bg-dark text-light mb-4">
				<div className="card-body">
					<label className="font-weight-bold">Excel file (.xlsx)</label>
					<input
						ref={fileInput}
						type="file"
						accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
						className="form-control-file mb-3"
						onChange={onFileChange}
						disabled={isBusy}
					/>
					{file ? <div className="small text-muted mb-3">Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</div> : null}
					{fileName ? <div className="small text-success mb-3">Last uploaded base name: <code>{fileName}</code></div> : null}

					<button
						type="button"
						className="btn btn-success mr-2"
						disabled={!file || isBusy}
						onClick={runAll}
					>
						{isBusy ? 'Running…' : 'Run all steps'}
						{isBusy ? <img src={loading} alt="" className="ml-2" style={{ height: 24, width: 24 }} /> : null}
					</button>
				</div>
			</div>

			<div className="card border-info mb-4">
				<div className="card-header">Step 4 — 3M API session tokens</div>
				<div className="card-body small">
					<p className="mb-2 font-weight-bold">Bookmarklets (recommended)</p>
					<p className="mb-2">Drag both links below to your bookmarks bar. While logged into b.com:</p>
					<ol className="mb-3 pl-3">
						<li className="mb-2">On <strong>order.3m.com</strong> (open the <a href={BCOM_URLS.delivery} target="_blank" rel="noopener noreferrer">productAvailability</a> page first), click <strong>EIO: 3M delivery token</strong> — copies <code>samlPassThroughToken</code>.</li>
						<li className="mb-2">On any <strong>b.com catalog</strong> page, click <strong>EIO: 3M dataset sniffer</strong>, then open a product — copies the Bearer JWT when the PDP API runs.</li>
						<li>Return here and use <strong>Paste from clipboard</strong> below (or paste into the fields).</li>
					</ol>
					<div className="d-flex flex-wrap mb-3">
						<a
							href={BCOM_BOOKMARKLET_DELIVERY_HREF}
							className="btn btn-sm btn-warning mr-2 mb-2"
							onClick={(e) => e.preventDefault()}
							title="Drag to bookmarks bar — do not click here"
						>
							⇢ EIO: 3M delivery token
						</a>
						<a
							href={BCOM_BOOKMARKLET_DATASET_HREF}
							className="btn btn-sm btn-warning mr-2 mb-2"
							onClick={(e) => e.preventDefault()}
							title="Drag to bookmarks bar — do not click here"
						>
							⇢ EIO: 3M dataset sniffer
						</a>
					</div>
					<p className="text-muted mb-2">
						Delivery cookie is <strong>HttpOnly</strong> — <code>document.cookie</code> cannot see it. The bookmarklet uses the <code>cookieStore</code> API on <strong>order.3m.com</strong> (Chrome/Edge). Run it from the bookmarks bar on that site, not from the EIO app tab.
					</p>
					<button type="button" className="btn btn-outline-secondary btn-sm mb-3" disabled={isBusy} onClick={copyDeliveryConsoleScript}>
						Copy console script (for order.3m.com DevTools)
					</button>
					<p className="mb-2 font-weight-bold">Manual (DevTools)</p>
					<ol className="mb-3 pl-3">
						<li className="mb-2">
							<strong>Delivery</strong> — Network tab on{' '}
							<a href={BCOM_URLS.delivery} target="_blank" rel="noopener noreferrer">productAvailability</a>
							, copy <code>samlPassThroughToken</code> (starts with <code>1:</code>).
						</li>
						<li>
							<strong>Dataset</strong> — find POST to <code>searchapi.3m.com/.../pdp</code>, copy <code>Authorization: Bearer …</code> (JWT only).
						</li>
					</ol>
					<div className="form-group">
						<label className="font-weight-bold">Dataset token (Bearer JWT)</label>
						<textarea className="form-control font-monospace small" rows={2} placeholder="eyJhbGciOiJIUzI1NiJ9..." value={datasetToken} onChange={(e) => setDatasetToken(e.target.value)} disabled={isBusy} />
					</div>
					<div className="form-group">
						<label className="font-weight-bold">Delivery token (samlPassThroughToken)</label>
						<textarea className="form-control font-monospace small" rows={2} placeholder="1:..." value={deliveryToken} onChange={(e) => setDeliveryToken(e.target.value)} disabled={isBusy} />
					</div>
					<div className="form-group">
						<label className="font-weight-bold">Or paste DevTools “Copy as fetch”</label>
						<textarea
							className="form-control font-monospace small"
							rows={3}
							placeholder="Network → productAvailability → right-click → Copy → Copy as fetch…"
							value={fetchDump}
							onChange={(e) => setFetchDump(e.target.value)}
							disabled={isBusy}
						/>
						<button type="button" className="btn btn-outline-primary btn-sm mt-2" disabled={isBusy || !fetchDump.trim()} onClick={parseDeliveryFromFetch}>
							Extract delivery token from fetch
						</button>
					</div>
					<button type="button" className="btn btn-outline-secondary btn-sm mr-2 mb-2" disabled={isBusy} onClick={() => pasteTokenFromClipboard('dataset')}>Paste dataset from clipboard</button>
					<button type="button" className="btn btn-outline-secondary btn-sm mr-2 mb-2" disabled={isBusy} onClick={() => pasteTokenFromClipboard('delivery')}>Paste delivery from clipboard</button>
					<button type="button" className="btn btn-outline-secondary btn-sm mr-2 mb-2" disabled={isBusy} onClick={saveTokens}>Save tokens in browser</button>
					<button type="button" className="btn btn-outline-secondary btn-sm mr-2 mb-2" disabled={isBusy} onClick={() => copyCliCommand('dataset')}>Copy CLI (dataset)</button>
					<button type="button" className="btn btn-outline-secondary btn-sm mb-2" disabled={isBusy} onClick={() => copyCliCommand('delivery')}>Copy CLI (delivery)</button>
					<p className="text-muted mt-3 mb-0">Tokens expire after a few hours. Downloads can take 30+ minutes — if the browser times out, run the same step again; progress is saved under <code>savedFiles/autoCreate/3M/bcom/privateApiDump/</code>.</p>
				</div>
			</div>

			<div className="row mb-4">
				{STEPS.map((step) => (
					<div key={step.id} className={`col-md-${step.id.startsWith('download') ? '6' : '4'} mb-3`}>
						<div className="card h-100 border-secondary">
							<div className="card-body">
								<h6>{step.label}{stepBadge(stepStatus[step.id])}</h6>
								<button
									type="button"
									className="btn btn-outline-primary btn-sm"
									disabled={
										isBusy
										|| (step.id === 'convert' && !fileName)
										|| (step.id === 'upload' && !file)
										|| (step.id === 'downloadDataset' && !normalizeDatasetToken(datasetToken))
										|| (step.id === 'downloadDelivery' && !normalizeDeliveryToken(deliveryToken))
									}
									onClick={() => runStep(step.id)}
								>
									Run step
								</button>
							</div>
						</div>
					</div>
				))}
			</div>

			{log.length > 0 ? (
				<div className="card border-secondary">
					<div className="card-header">Log</div>
					<pre className="card-body small mb-0" style={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
						{log.map((line, i) => (
							<div key={i} className={line.type === 'error' ? 'text-danger' : line.type === 'success' ? 'text-success' : line.type === 'warn' ? 'text-warning' : ''}>
								[{line.at}] {line.msg}
							</div>
						))}
					</pre>
				</div>
			) : null}
		</div>
	)
}
